import React, { useState, useEffect } from "react";
import { getItemsbyType } from "../service/APIService";

const UserStats = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch all three data sets in parallel
      const [users, recipes, uploads] = await Promise.all([
        getItemsbyType("appusers"),
        getItemsbyType("recipe-ai"),
        getItemsbyType("recipe-ai-food-calorie")
      ]);

      // Create aggregation map
      const userMap = {};

      // Initialize with users
      users.forEach(user => {
        userMap[user.email] = {
          name: user.name || "N/A",
          email: user.email,
          recipes: 0,
          uploads: 0
        };
      });

      // Count recipes
      recipes.forEach(recipe => {
        if (userMap[recipe.email]) {
          userMap[recipe.email].recipes += 1;
        } else if (recipe.email) {
          // Handle case where item exists but user is not in appusers list (e.g. legacy or guest)
          userMap[recipe.email] = { name: "Unknown", email: recipe.email, recipes: 1, uploads: 0 };
        }
      });

      // Count uploads
      uploads.forEach(upload => {
        if (userMap[upload.email]) {
          userMap[upload.email].uploads += 1;
        } else if (upload.email) {
          if (!userMap[upload.email]) {
            userMap[upload.email] = { name: "Unknown", email: upload.email, recipes: 0, uploads: 1 };
          } else {
            userMap[upload.email].uploads += 1;
          }
        }
      });

      // Convert map to array and sort by recipe count desc
      const statsArray = Object.values(userMap).sort((a, b) => b.recipes - a.recipes);
      setStats(statsArray);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch user stats:", error);
      setLoading(false);
    }
  };

  const filteredStats = stats.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mt-5 pt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0">Engagement Stats</h5>
        <button className="btn btn-outline-success btn-sm" onClick={fetchStats} disabled={loading}>
          <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
        </button>
      </div>

      <div className="mb-3">
        <input 
          type="text" 
          className="form-control form-control-sm" 
          placeholder="Search user..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center p-4 border rounded bg-light">
          <i className="fas fa-spinner fa-spin text-success mb-2"></i>
          <p className="small mb-0">Analyzing data...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="d-none d-md-block table-responsive shadow-sm rounded border bg-white">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-3">Name</th>
                  <th>Email</th>
                  <th className="text-center">Recipes</th>
                  <th className="text-center">Uploads</th>
                </tr>
              </thead>
              <tbody>
                {filteredStats.length > 0 ? (
                  filteredStats.map((user, index) => (
                    <tr key={index}>
                      <td className="px-3"><span className="fw-bold text-dark">{user.name}</span></td>
                      <td><small className="text-muted">{user.email}</small></td>
                      <td className="text-center">
                        <span className="badge rounded-pill bg-primary">{user.recipes}</span>
                      </td>
                      <td className="text-center">
                        <span className="badge rounded-pill bg-info text-white">{user.uploads}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center p-4 text-muted small">No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View (Compact) */}
          <div className="d-md-none">
            {filteredStats.length > 0 ? (
              filteredStats.map((user, index) => (
                <div key={index} className="card mb-2 shadow-sm border-0 border-bottom">
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1 text-truncate pe-2">
                        <div className="fw-bold small text-dark mb-0">{user.name}</div>
                        <div className="text-muted" style={{fontSize: '0.75rem'}}>{user.email}</div>
                      </div>
                      <div className="d-flex gap-1">
                        <div className="text-center">
                          <div style={{fontSize: '0.65rem'}} className="text-muted uppercase">REC</div>
                          <span className="badge bg-primary px-2" style={{fontSize: '0.7rem'}}>{user.recipes}</span>
                        </div>
                        <div className="text-center">
                          <div style={{fontSize: '0.65rem'}} className="text-muted uppercase">CAL</div>
                          <span className="badge bg-info text-white px-2" style={{fontSize: '0.7rem'}}>{user.uploads}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center p-3 border rounded bg-light text-muted small">
                No users found.
              </div>
            )}
          </div>
        </>
      )}
      
      <div className="mt-3 text-end">
        <small className="text-muted">Total Users Analyzed: {stats.length}</small>
      </div>
    </div>
  );
};

export default UserStats;
